import { apiDelete, apiGet, apiPost, apiPut } from '@/shared/utils/requestBuilder';
import type { CreateSetorDto, Setor, SetorFilters, UpdateSetorDto } from '@/types/setor/setorTypes';

export const setorService = {
  listar: async (filters?: SetorFilters): Promise<{ data: Setor[]; total: number }> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.escritorioId !== undefined)
      params.append('escritorioId', filters.escritorioId || '');
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiGet(`/setores${query}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao listar setores' }));
      throw new Error(errorData.error || 'Erro ao listar setores');
    }

    const result = await response.json();
    return {
      data: result.data.data || result.data,
      total: result.data.pagination?.total || 0,
    };
  },

  buscarPorId: async (id: string): Promise<Setor> => {
    const response = await apiGet(`/setores/${id}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Setor não encontrado' }));
      throw new Error(errorData.error || 'Setor não encontrado');
    }

    const result = await response.json();
    return result.data;
  },

  criar: async (data: CreateSetorDto): Promise<Setor> => {
    const response = await apiPost('/setores', data);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao criar setor' }));
      throw new Error(errorData.error || 'Erro ao criar setor');
    }

    const result = await response.json();
    return result.data;
  },

  atualizar: async (id: string, data: UpdateSetorDto): Promise<Setor> => {
    const response = await apiPut(`/setores/${id}`, data);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao atualizar setor' }));
      throw new Error(errorData.error || 'Erro ao atualizar setor');
    }

    const result = await response.json();
    return result.data;
  },

  excluir: async (id: string): Promise<void> => {
    const response = await apiDelete(`/setores/${id}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao excluir setor' }));
      throw new Error(errorData.error || 'Erro ao excluir setor');
    }
  },

  buscarEstatisticas: async (
    id: string,
  ): Promise<{
    totalExecucoes: number;
    valorTotal: number;
    execucoesAtivas: number;
    execucoesPausadas: number;
    execucoesEncerradas: number;
  }> => {
    const response = await apiGet(`/setores/${id}/statistics`);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Erro ao buscar estatísticas' }));
      throw new Error(errorData.error || 'Erro ao buscar estatísticas');
    }

    const result = await response.json();
    return result.data;
  },

  buscarTimeline: async (
    id: string,
    filters?: {
      page?: number;
      limit?: number;
      tipoEvento?: string;
    },
  ): Promise<{
    data: Array<{
      id: string;
      tipoEvento: string;
      descricao: string;
      usuarioId: string | null;
      usuarioNome: string;
      usuarioEmail: string;
      dataEvento: string;
      metadados: Record<string, unknown>;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> => {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.tipoEvento) params.append('tipoEvento', filters.tipoEvento);

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiGet(`/setores/${id}/timeline${query}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao buscar timeline' }));
      throw new Error(errorData.error || 'Erro ao buscar timeline');
    }

    const result = await response.json();
    return result.data;
  },

  listarUsuarios: async (
    setorId: string,
    search?: string,
  ): Promise<
    Array<{
      id: string;
      nomeCompleto: string;
      email: string;
      tipoUsuario: string;
      ativo: boolean;
    }>
  > => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiGet(`/setores/${setorId}/usuarios${query}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao listar usuários' }));
      throw new Error(errorData.error || 'Erro ao listar usuários');
    }

    const result = await response.json();
    return result.data;
  },

  listarUsuariosDisponiveis: async (
    setorId: string,
    search?: string,
  ): Promise<
    Array<{
      id: string;
      nomeCompleto: string;
      email: string;
      tipoUsuario: string;
      ativo: boolean;
    }>
  > => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiGet(`/setores/${setorId}/usuarios/disponiveis${query}`);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Erro ao listar usuários disponíveis' }));
      throw new Error(errorData.error || 'Erro ao listar usuários disponíveis');
    }

    const result = await response.json();
    return result.data;
  },

  adicionarUsuario: async (setorId: string, usuarioId: string): Promise<void> => {
    const response = await apiPost(`/setores/${setorId}/usuarios`, { usuarioId });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao adicionar usuário' }));
      throw new Error(errorData.error || 'Erro ao adicionar usuário');
    }
  },

  removerUsuario: async (setorId: string, usuarioId: string): Promise<void> => {
    const response = await apiDelete(`/setores/${setorId}/usuarios/${usuarioId}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao remover usuário' }));
      throw new Error(errorData.error || 'Erro ao remover usuário');
    }
  },

  listarExecucoes: async (
    setorId: string,
    search?: string,
  ): Promise<
    Array<{
      id: string;
      numeroProcesso: string;
      titulo: string;
      status: string;
      valorCausa: number | null;
      dataCriacao: string;
    }>
  > => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiGet(`/setores/${setorId}/execucoes${query}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao listar execuções' }));
      throw new Error(errorData.error || 'Erro ao listar execuções');
    }

    const result = await response.json();
    return result.data;
  },

  listarExecucoesDisponiveis: async (
    setorId: string,
    search?: string,
  ): Promise<
    Array<{
      id: string;
      numeroProcesso: string;
      titulo: string;
      status: string;
      valorCausa: number | null;
      dataCriacao: string;
    }>
  > => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiGet(`/setores/${setorId}/execucoes/disponiveis${query}`);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Erro ao listar execuções disponíveis' }));
      throw new Error(errorData.error || 'Erro ao listar execuções disponíveis');
    }

    const result = await response.json();
    return result.data;
  },

  adicionarExecucao: async (setorId: string, execucaoId: string): Promise<void> => {
    const response = await apiPost(`/setores/${setorId}/execucoes`, { execucaoId });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao vincular execução' }));
      throw new Error(errorData.error || 'Erro ao vincular execução');
    }
  },

  removerExecucao: async (setorId: string, execucaoId: string): Promise<void> => {
    const response = await apiDelete(`/setores/${setorId}/execucoes/${execucaoId}`);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Erro ao desvincular execução' }));
      throw new Error(errorData.error || 'Erro ao desvincular execução');
    }
  },

  enviarConvite: async (
    setorId: string,
    data: {
      email: string;
      papel?: 'ADMIN' | 'EDITOR' | 'VIEWER';
      observacoes?: string;
    },
  ): Promise<{
    id: string;
    email: string;
    token: string;
    expiraEm: string;
  }> => {
    const response = await apiPost(`/setores/${setorId}/convites`, data);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao enviar convite' }));
      throw new Error(errorData.error || 'Erro ao enviar convite');
    }

    const result = await response.json();
    return result.data;
  },

  listarConvites: async (
    setorId: string,
    filters?: {
      usado?: boolean;
      cancelado?: boolean;
    },
  ): Promise<
    Array<{
      id: string;
      email: string;
      papel: string;
      token: string;
      expiraEm: string;
      usado: boolean;
      usadoEm: string | null;
      cancelado: boolean;
      dataCriacao: string;
      observacoes: string | null;
      convidador: {
        nomeCompleto: string;
      };
    }>
  > => {
    const params = new URLSearchParams();
    if (filters?.usado !== undefined) params.append('usado', String(filters.usado));
    if (filters?.cancelado !== undefined) params.append('cancelado', String(filters.cancelado));

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiGet(`/setores/${setorId}/convites${query}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao listar convites' }));
      throw new Error(errorData.error || 'Erro ao listar convites');
    }

    const result = await response.json();
    return result.data;
  },

  cancelarConvite: async (conviteId: string): Promise<void> => {
    const response = await apiDelete(`/convites-escritorio/escritorio/global/${conviteId}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao cancelar convite' }));
      throw new Error(errorData.error || 'Erro ao cancelar convite');
    }
  },
};
